import { useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";

const PAGE_SIZE = 10;

export default function ProductCopyPanel({ tenants }) {
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("all");
  const [confirmed, setConfirmed] = useState(false);

  const label = (id) => {
    const tenant = tenants.find(item => item.id === id);
    return tenant ? `${tenant.name} (${tenant.host})` : id;
  };
  function change(setter, value) {
    setter(value); setPreview(null); setResult(null); setError(""); setConfirmed(false); setPage(0);
  }
  async function check() {
    setBusy(true); setError(""); setPreview(null); setResult(null); setConfirmed(false); setPage(0);
    try {
      const { data } = await api.post("/master/product-copy/preview", { source, destination });
      setPreview(data);
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  }
  async function copy() {
    if (!preview || !confirmed || busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/master/product-copy/apply", {
        source: preview.source, destination: preview.destination, fingerprint: preview.fingerprint,
      });
      setResult(data); setPreview(null);
    } catch (e) {
      setError(`${apiError(e)} Periksa ulang pratinjau untuk melihat produk yang masih belum ada di tujuan.`);
      setPreview(null);
    } finally { setBusy(false); setConfirmed(false); }
  }

  const rows = (preview?.items || []).filter(item => filter === "all" || item.status === filter);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  return <section aria-labelledby="product-copy-title" data-testid="product-copy-panel" className="bg-ui-card border border-ui-line rounded-xl p-4 sm:p-5 space-y-4 min-w-0">
    <div>
      <h2 id="product-copy-title" className="text-lg font-semibold">Salin Produk Antartoko</h2>
      <p className="text-sm text-ui-muted mt-1">Tambahkan produk yang belum ada di toko tujuan. Produk dengan nama atau barcode/SKU yang sudah digunakan akan dilewati.</p>
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm min-w-0">Toko asal
        <select data-testid="copy-source" value={source} disabled={busy} onChange={e => change(setSource, e.target.value)} className="block w-full mt-1 border border-ui-line rounded-lg bg-ui-card p-2 min-h-11">
          <option value="">Pilih toko asal</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{label(t.id)}</option>)}
        </select>
      </label>
      <label className="text-sm min-w-0">Toko tujuan
        <select data-testid="copy-destination" value={destination} disabled={busy} onChange={e => change(setDestination, e.target.value)} className="block w-full mt-1 border border-ui-line rounded-lg bg-ui-card p-2 min-h-11">
          <option value="">Pilih toko tujuan</option>
          {tenants.map(t => <option key={t.id} value={t.id} disabled={t.id === source}>{label(t.id)}</option>)}
        </select>
      </label>
    </div>
    <p className="text-sm text-ui-muted">Disalin: nama, barcode/SKU, kategori, satuan dan isi kemasan, varian, harga jual, serta pengaturan aktif produk. Stok dan modal awal produk baru = 0. Foto, supplier, pembelian, dan transaksi tidak ikut disalin.</p>
    <Button type="button" data-testid="copy-preview" onClick={check} disabled={busy || !source || !destination || source === destination}>{busy ? "Memproses…" : "Periksa Produk yang Belum Ada"}</Button>
    {source && source === destination && <p role="alert" className="text-danger-readable text-sm">Pilih dua toko yang berbeda.</p>}
    {error && <p role="alert" className="bg-danger-soft text-danger-readable p-3 rounded-lg break-words">{error}</p>}
    {result && <div role="status" data-testid="copy-result" className="bg-brand-soft text-brand-readable p-3 rounded-lg">
      <strong>{result.created} produk berhasil disalin.</strong>
      <p className="break-words">{label(result.source)} → {label(result.destination)}</p>
      <p>{result.skipped} produk dilewati. Produk tujuan yang sudah ada tidak diubah.</p>
    </div>}
    {preview && <div data-testid="copy-preview-results" className="space-y-3">
      <p className="font-semibold break-words">{label(preview.source)} → {label(preview.destination)}</p>
      <p role="status">{preview.total} produk diperiksa · {preview.ready} siap disalin · {preview.skipped} dilewati</p>
      <label className="text-sm flex flex-wrap items-center gap-2">Tampilkan
        <select value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }} className="border border-ui-line rounded-lg p-2 bg-ui-card">
          <option value="all">Semua produk</option><option value="ready">Siap disalin</option><option value="skipped">Dilewati</option>
        </select>
      </label>
      <div className="overflow-x-auto border border-ui-line rounded-lg">
        <table className="w-full text-sm min-w-[540px]">
          <thead className="bg-ui-soft"><tr><th className="p-3 text-left">Produk / Barcode</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Keterangan</th></tr></thead>
          <tbody>{rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(item => <tr key={item.source_id} className="border-t border-ui-line" data-testid="copy-product-row">
            <td className="p-3 break-words"><strong>{item.name || "Tanpa nama"}</strong><div className="text-ui-muted">{item.barcode || "—"}</div></td>
            <td className="p-3 whitespace-nowrap">{item.status === "ready" ? "Siap disalin" : "Dilewati"}</td>
            <td className="p-3">{item.reasons.length ? item.reasons.join(" ") : "Produk baru di tujuan; stok dan modal awal 0."}</td>
          </tr>)}</tbody>
        </table>
        {!rows.length && <p className="p-3 text-ui-muted">Tidak ada produk dalam daftar ini.</p>}
      </div>
      <Pagination aria-label="Halaman pratinjau produk">
        <PaginationContent>
          <PaginationItem><Button type="button" variant="outline" size="sm" disabled={!page} onClick={() => setPage(p => p - 1)}>Sebelumnya</Button></PaginationItem>
          <PaginationItem><span className="text-sm px-2">{page + 1} / {pages}</span></PaginationItem>
          <PaginationItem><Button type="button" variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Berikutnya</Button></PaginationItem>
        </PaginationContent>
      </Pagination>
      {preview.ready > 0 ? <>
        <label className="flex items-start gap-2 text-sm py-2"><input type="checkbox" data-testid="copy-confirm" checked={confirmed} disabled={busy} onChange={e => setConfirmed(e.target.checked)} className="mt-1" /><span>Saya sudah memeriksa toko tujuan dan setuju menambahkan {preview.ready} produk baru.</span></label>
        <Button type="button" data-testid="copy-apply" disabled={busy || !confirmed} onClick={copy}>{busy ? "Menyalin…" : `Salin ${preview.ready} Produk ke Tujuan`}</Button>
      </> : <p className="text-sm text-ui-muted">Tidak ada produk baru yang dapat disalin. Lihat alasan produk yang dilewati.</p>}
    </div>}
  </section>;
}
